// ============================================================
// ByOurConsent.com -- Cart Context
// Guests: sessionStorage (clears on browser close)
// Logged in: localStorage (persists)
// ============================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

const GUEST_KEY = 'boc_cart_guest';

const getUserId = () => {
    try {
        const u = JSON.parse(localStorage.getItem('boc_user') || 'null');
        return u?.id || null;
    } catch { return null; }
};

const isLoggedIn = () => !!localStorage.getItem('boc_token');

const getUserCartKey = () => {
    const uid = getUserId();
    return uid ? `boc_cart_${uid}` : null;
};

const readCart = () => {
    try {
        if (isLoggedIn()) {
            const key = getUserCartKey();
            const raw = key ? localStorage.getItem(key) : null;
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : (parsed.items || []);
        } else {
            const raw = sessionStorage.getItem(GUEST_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : (parsed.items || []);
        }
    } catch { return []; }
};

const writeCart = (items) => {
    try {
        const data = JSON.stringify({ items, ts: Date.now() });
        if (isLoggedIn()) {
            const key = getUserCartKey();
            if (key) localStorage.setItem(key, data);
            sessionStorage.removeItem(GUEST_KEY);
        } else {
            sessionStorage.setItem(GUEST_KEY, data);
        }
    } catch {}
};

const clearCartStorage = () => {
    const key = getUserCartKey();
    if (key) localStorage.removeItem(key);
    sessionStorage.removeItem(GUEST_KEY);
};

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState(() => readCart());

    // Persist whenever cart changes
    useEffect(() => {
        if (cart.length > 0) {
            writeCart(cart);
        } else {
            clearCartStorage();
        }
    }, [cart]);

    // Listen for login/logout cart events
    useEffect(() => {
        const handleClear = () => setCart([]);
        const handleLogin = (e) => {
            // Load user's saved cart
            const userId = e.detail?.userId;
            if (userId) {
                try {
                    const raw = localStorage.getItem(`boc_cart_${userId}`);
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        const items = Array.isArray(parsed) ? parsed : (parsed.items || []);
                        setCart(items);
                        return;
                    }
                } catch {}
            }
            setCart([]);
        };
        window.addEventListener('cart:clear', handleClear);
        window.addEventListener('cart:login', handleLogin);
        return () => {
            window.removeEventListener('cart:clear', handleClear);
            window.removeEventListener('cart:login', handleLogin);
        };
    }, []);

    // When user logs in — migrate sessionStorage cart to localStorage
    // When user logs out — clear localStorage cart
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key === 'boc_token') {
                if (e.newValue) {
                    // Logged in — migrate session cart to local
                    const sessionCart = readCart();
                    if (sessionCart.length > 0) {
                        const ukey = getUserCartKey(); if (ukey) localStorage.setItem(ukey, JSON.stringify({ items: sessionCart, ts: Date.now() }));
                        sessionStorage.removeItem(GUEST_KEY);
                        setCart(sessionCart);
                    } else {
                        // Check if logged-in user has a saved cart
                        const ukey2 = getUserCartKey(); const localRaw = ukey2 ? localStorage.getItem(ukey2) : null;
                        if (localRaw) {
                            try {
                                const parsed = JSON.parse(localRaw);
                                setCart(Array.isArray(parsed) ? parsed : (parsed.items || []));
                            } catch {}
                        }
                    }
                } else {
                    // Logged out — clear cart entirely
                    clearCartStorage();
                    setCart([]);
                }
            }
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    const addItem = useCallback((item) => {
        setCart(prev => {
            const idx = prev.findIndex(
                c => c.product_id === item.product_id && c.variant_id === item.variant_id
            );
            if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + item.quantity };
                return updated;
            }
            return [...prev, item];
        });
    }, []);

    const removeItem = useCallback((productId, variantId) => {
        setCart(prev => prev.filter(
            c => !(c.product_id === productId && c.variant_id === variantId)
        ));
    }, []);

    const updateQty = useCallback((productId, variantId, qty) => {
        if (qty < 1) { removeItem(productId, variantId); return; }
        setCart(prev => prev.map(c =>
            c.product_id === productId && c.variant_id === variantId
                ? { ...c, quantity: qty } : c
        ));
    }, [removeItem]);

    const clearCart = useCallback(() => {
        setCart([]);
        // Clear all possible cart storage keys
        sessionStorage.removeItem(GUEST_KEY);
        const key = getUserCartKey();
        if (key) localStorage.removeItem(key);
        localStorage.removeItem('boc_cart');
        sessionStorage.removeItem('boc_cart');
        window.dispatchEvent(new Event('cart:clear'));
    }, []);

    const cartCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
    const cartTotal = cart.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (item.quantity || 1), 0);

    return (
        <CartContext.Provider value={{
            cart, addItem, removeItem, updateQty, clearCart,
            cartCount, cartTotal
        }}>
            {children}
        </CartContext.Provider>
    );
};
